import type {ReactElement} from 'react';
import {useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {useObjectUrl} from '@Hooks/useObjectUrl';
import {assertNever} from '@Utils/assert-never.util';
import type {AiContentVerifyFilePreviewProps} from './ai-content-verify-file-preview.types';
import {AUDIO_MIME_PREFIX, FilePreviewKind, HTML_ICON, IMAGE_MIME_PREFIX, PDF_ICON, PDF_MIME_TYPE, VIDEO_MIME_PREFIX} from './ai-content-verify-file-preview.types';
import styles from './ai-content-verify-file-preview.module.scss';

export function AiContentVerifyFilePreview({file}: AiContentVerifyFilePreviewProps): ReactElement {
  const objectUrl = useObjectUrl(file);
  const [hasMediaFailed, setHasMediaFailed] = useState(false);

  const onMediaError = (): void => {
    setHasMediaFailed(true);
  };

  const renderDocumentTile = (): ReactElement => {
    return (
      <div className={styles.filePreviewDocument}>
        <Icon icon={getDocumentIcon(file)} size={IconSize.SIZE_ICON_48} className={styles.filePreviewDocumentIcon} ariaHidden />
        <Text val={file.name} size={TextSize.MD} bold className={styles.filePreviewDocumentName} />
      </div>
    );
  };

  const renderPreview = (): ReactElement => {
    const previewKind = getPreviewKind(file);

    if (!objectUrl || hasMediaFailed || previewKind === FilePreviewKind.DOCUMENT) {
      return renderDocumentTile();
    }

    switch (previewKind) {
      case FilePreviewKind.IMAGE:
        return <img src={objectUrl} alt={file.name} className={styles.filePreviewMedia} onError={onMediaError} />;
      case FilePreviewKind.VIDEO:
        return <video src={objectUrl} className={styles.filePreviewMedia} controls muted playsInline preload="metadata" aria-label={file.name} onError={onMediaError} />;
      case FilePreviewKind.AUDIO:
        return <audio src={objectUrl} className={styles.filePreviewAudio} controls preload="metadata" aria-label={file.name} onError={onMediaError} />;
      default:
        return assertNever(previewKind);
    }
  };

  return <div className={styles.filePreview}>{renderPreview()}</div>;
}

function getPreviewKind(file: File): FilePreviewKind {
  if (file.type.startsWith(IMAGE_MIME_PREFIX)) {
    return FilePreviewKind.IMAGE;
  }

  if (file.type.startsWith(VIDEO_MIME_PREFIX)) {
    return FilePreviewKind.VIDEO;
  }

  if (file.type.startsWith(AUDIO_MIME_PREFIX)) {
    return FilePreviewKind.AUDIO;
  }

  return FilePreviewKind.DOCUMENT;
}

function getDocumentIcon(file: File): string {
  if (file.type === PDF_MIME_TYPE) {
    return PDF_ICON;
  }

  return HTML_ICON;
}
